import React, { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { digitalArt } from '../../data/mock';
import Lightbox from '../Lightbox';

/**
 * Masonry (CSS multi-column) layout — preserves each image's natural aspect
 * ratio. Wide images stay wide, tall ones stay tall, squares stay square.
 * The article body scrolls vertically so no tiles get clipped; horizontal
 * overflow is locked off to prevent stray scrollbars from column math.
 */
export default function Illustrations() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section className="xl:h-full w-full xl:overflow-hidden px-4 sm:px-6 md:px-10 lg:px-12 py-3 md:py-5 flex flex-col">
      {/* Section header — carries entry count + meta chips + IG link so the article body is pure gallery */}
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] sm:text-[13px]" style={{ color: 'var(--amber)', letterSpacing: '0.15em' }}>
        <span className="font-semibold">ILLUSTRATIONS</span>
        <span className="flex-1 min-w-[24px] h-px" style={{ background: 'rgba(245,165,36,0.2)' }} />
        <span className="opacity-70 whitespace-nowrap normal-case tracking-normal">{digitalArt.length} entries</span>
        <span className="hidden md:inline opacity-30">|</span>
        <div className="hidden md:flex items-center gap-1.5">
          <span className="text-[11px] px-2 py-0.5 chip" style={{ border: '1px solid rgba(245,165,36,0.45)', color: 'var(--amber)', borderRadius: 2, letterSpacing: '0.06em' }}>illustration</span>
          <span className="text-[11px] px-2 py-0.5 chip" style={{ border: '1px solid rgba(245,165,36,0.45)', color: 'var(--amber)', borderRadius: 2, letterSpacing: '0.06em' }}>sketchbook</span>
          <span className="text-[11px] px-2 py-0.5 chip" style={{ border: '1px solid rgba(245,165,36,0.45)', color: 'var(--amber)', borderRadius: 2, letterSpacing: '0.06em' }}>concept-art</span>
        </div>
        <a
          href="https://www.instagram.com/_sakshamshukla_/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="illustrations-instagram-link"
          className="link-amber text-[12px] normal-case tracking-normal whitespace-nowrap"
        >
          Instagram →
        </a>
      </div>

      <article
        className="glass-card rounded-sm p-3 sm:p-4 flex flex-col xl:flex-1 xl:min-h-0"
        style={{ border: '1px solid var(--amber)', overflowX: 'hidden' }}
      >
        {/* Masonry column layout — each tile keeps its intrinsic aspect ratio */}
        <div
          className="xl:flex-1 xl:min-h-0 xl:overflow-y-auto stagger"
          style={{ overflowX: 'hidden' }}
          data-testid="illustrations-masonry"
        >
          <style>{`
            .illos-masonry { column-count: 2; column-gap: 10px; }
            @media (min-width: 900px)  { .illos-masonry { column-count: 3; } }
            @media (min-width: 1280px) { .illos-masonry { column-count: 4; } }
            @media (min-width: 1700px) { .illos-masonry { column-count: 5; } }
            .illos-tile {
              break-inside: avoid;
              -webkit-column-break-inside: avoid;
              page-break-inside: avoid;
              margin: 0 0 10px;
              display: block;
              max-width: 100%;
            }
          `}</style>

          <div className="illos-masonry">
            {digitalArt.map((a) => (
              <figure
                key={a.id}
                className="illos-tile relative rounded-sm overflow-hidden group card-hover cursor-pointer"
                style={{ border: '1px solid rgba(245,165,36,0.5)', background: '#050301' }}
                onClick={() => setLightbox({ src: a.src, caption: a.title, type: 'image' })}
                data-testid={`illustration-tile-${a.id}`}
              >
                <img
                  src={a.src}
                  alt={a.title}
                  loading="lazy"
                  width={a.w}
                  height={a.h}
                  className="block w-full h-auto max-w-full transition-transform duration-500 group-hover:scale-[1.03]"
                  style={{ aspectRatio: a.w && a.h ? `${a.w} / ${a.h}` : undefined }}
                  data-testid={`illustration-img-${a.id}`}
                />

                {/* Expand affordance */}
                <button
                  type="button"
                  className="absolute top-2 right-2 w-8 h-8 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                  style={{
                    border: '1px solid rgba(245,165,36,0.55)',
                    color: 'var(--amber)',
                    background: 'rgba(5,3,1,0.7)',
                    borderRadius: 4,
                    backdropFilter: 'blur(4px)'
                  }}
                  aria-label={`Expand ${a.title}`}
                  data-testid={`illustration-expand-${a.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox({ src: a.src, caption: a.title, type: 'image' });
                  }}
                >
                  <Maximize2 size={13} strokeWidth={2} />
                </button>

                <figcaption
                  className="absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] flex items-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    color: 'var(--amber)',
                    background: 'linear-gradient(180deg, transparent, rgba(5,5,5,0.92))',
                    letterSpacing: '0.05em'
                  }}
                >
                  <span>#{String(a.id).padStart(2, '0')}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </article>

      {lightbox && <Lightbox media={lightbox} onClose={() => setLightbox(null)} />}
    </section>
  );
}
